import { baseApi, defaultQueryFreshness } from "./baseApi";

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<unknown[], string>({
      query: (userId) => ({ url: `/notifications/${userId}`, method: "GET" }),
      providesTags: (result, _err, userId) => [
        { type: "Notifications", id: `list:${userId}` },
        ...(result || []).map((item: any) => ({ type: "Notifications" as const, id: String(item.id) })),
      ],
      ...defaultQueryFreshness,
    }),
    getUnreadCount: builder.query<{ count?: number } | number | unknown, string>({
      query: (userId) => ({ url: `/notifications/${userId}/unread-count`, method: "GET" }),
      providesTags: (_res, _err, userId) => [{ type: "Notifications", id: `unread:${userId}` }],
      ...defaultQueryFreshness,
    }),
    markNotificationAsRead: builder.mutation<unknown, { userId: string; notificationId: string }>({
      query: ({ notificationId }) => ({ url: `/notifications/${notificationId}/read`, method: "PATCH" }),
      async onQueryStarted({ userId, notificationId }, { dispatch, queryFulfilled }) {
        const patchList = dispatch(
          notificationsApi.util.updateQueryData("getNotifications", userId, (draft: any[]) => {
            const n = draft?.find((item) => String(item.id) === String(notificationId));
            if (n) n.read = true;
          }),
        );
        const patchCount = dispatch(
          notificationsApi.util.updateQueryData("getUnreadCount", userId, (draft: any) => {
            if (typeof draft === "number") return Math.max(0, draft - 1);
            if (draft && typeof draft === "object" && typeof draft.count === "number") {
              draft.count = Math.max(0, draft.count - 1);
            }
            return draft;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchList.undo();
          patchCount.undo();
        }
      },
      invalidatesTags: (_res, _err, { userId, notificationId }) => [
        { type: "Notifications", id: String(notificationId) },
        { type: "Notifications", id: `unread:${userId}` },
      ],
    }),
    markAllAsRead: builder.mutation<unknown, { userId: string }>({
      query: ({ userId }) => ({ url: `/notifications/${userId}/mark-all-read`, method: "PATCH" }),
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        const patchList = dispatch(
          notificationsApi.util.updateQueryData("getNotifications", userId, (draft: any[]) => {
            (draft || []).forEach((item) => {
              item.read = true;
            });
          }),
        );
        const patchCount = dispatch(
          notificationsApi.util.updateQueryData("getUnreadCount", userId, (_draft: any) => {
            return { count: 0 };
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchList.undo();
          patchCount.undo();
        }
      },
      invalidatesTags: (_res, _err, { userId }) => [
        { type: "Notifications", id: `list:${userId}` },
        { type: "Notifications", id: `unread:${userId}` },
      ],
    }),
    deleteNotification: builder.mutation<unknown, { userId: string; notificationId: string }>({
      query: ({ notificationId }) => ({ url: `/notifications/${notificationId}`, method: "DELETE" }),
      invalidatesTags: (_res, _err, { userId, notificationId }) => [
        { type: "Notifications", id: String(notificationId) },
        { type: "Notifications", id: `list:${userId}` },
        { type: "Notifications", id: `unread:${userId}` },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
} = notificationsApi;
